import { Component, type ReactNode } from 'react';
import {
  isPresentationBlock,
  type PresentationBlock,
  type TablePresentation,
} from '../../types/presentation.types';
import type { OpenRecordFn } from '../../hooks/use-open-record';
import { AgentDetailCard } from './agent-detail-card';
import { AgentMetricCards } from './agent-metric-cards';
import { AgentTimeline } from './agent-timeline';
import { AgentEmptyState } from './agent-empty-state';
import { AgentTableViewButton } from './agent-table-view-button';

/**
 * Catches any render-time throw from a single presentation block so a malformed
 * block can never crash the chat thread. Renders nothing on failure.
 */
class BlockBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function renderBlock(
  block: PresentationBlock,
  onOpenRecord?: OpenRecordFn,
  headline?: string,
  onOpenTable?: (block: TablePresentation) => void
) {
  switch (block.kind) {
    case 'table':
      // Tables open in the left modal via View — never inline in the thread.
      return onOpenTable ? (
        <AgentTableViewButton
          block={block}
          onOpen={onOpenTable}
        />
      ) : null;
    case 'detail_card':
      return (
        <AgentDetailCard
          block={block}
          headline={headline}
        />
      );
    case 'metric_cards':
      return <AgentMetricCards block={block} />;
    case 'timeline':
      return <AgentTimeline block={block} />;
    case 'empty_state':
      return <AgentEmptyState block={block} />;
    // Fail-safe: unknown kinds render nothing, never throw.
    default:
      return null;
  }
}

/**
 * Renders one structured presentation block as its native CRM component.
 * Unknown/malformed blocks render nothing (fail-safe): the runtime guard
 * rejects anything that isn't a valid block, and an error boundary swallows any
 * render throw from a well-typed-but-hostile payload.
 */
export function PresentationRenderer({
  block,
  onOpenRecord,
  headline,
  onOpenTable,
}: {
  block: unknown;
  onOpenRecord?: OpenRecordFn;
  /** Optional success/status line shown with detail_card (Figma green title). */
  headline?: string;
  /** Opens the left table modal for `kind: 'table'` blocks. */
  onOpenTable?: (block: TablePresentation) => void;
}) {
  if (!isPresentationBlock(block)) return null;
  return (
    <BlockBoundary>
      {renderBlock(block, onOpenRecord, headline, onOpenTable)}
    </BlockBoundary>
  );
}
